import { useMemo } from 'react';

/**
 * useFormButtonDisabled
 * Returns true if:
 * 1. Any required field is empty
 * 2. All fields are unchanged from initial values
 *
 * @param values - current form values
 * @param initialValues - initial form values
 * @param requiredFields - array of required field names
 */
export function useFormButtonDisabled<T extends Record<string, unknown>>({
  values,
  initialValues,
  requiredFields,
}: {
  values: T;
  initialValues: T;
  requiredFields: (keyof T)[];
}): boolean {
  return useMemo(() => {
    // 1. Check required fields
    for (const field of requiredFields) {
      if (
        values[field] === undefined ||
        values[field] === null ||
        (typeof values[field] === 'string' && values[field].trim() === '')
      ) {
        return true;
      }
    }
    // 2. Check if all fields are unchanged
    const allUnchanged = requiredFields.every(
      (field) => values[field] === initialValues[field],
    );
    if (allUnchanged) return true;
    return false;
  }, [values, initialValues, requiredFields]);
}
